import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { LiveDot, PHONE_DISPLAY, PHONE_TEL } from "./Nav";

const BLACKLIGHT_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_e0816016-db79-423a-a550-6522d024345b/artifacts/yenw4l02_blacklight.webp";

const LINES = ["PRECISION", "INSPECTION.", "ANYWHERE.", "ANYTIME."];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="hero-section">
      {/* Parallax blacklight backdrop */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" aria-hidden="true">
        <img src={BLACKLIGHT_URL} alt="" className="h-full w-full object-cover object-center" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-machine via-machine/55 to-machine/35" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-machine/80 via-transparent to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1700px] px-4 pb-10 pt-32 sm:px-6 lg:px-10">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
          className="mb-8 flex flex-wrap items-center gap-4"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-uv" data-testid="hero-overline">
            Tube Tech Services INC
          </span>
          <span className="h-px w-16 bg-white/25" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-slate-400">
            NDT Inspection — Transportation
          </span>
        </motion.div>

        {/* Kinetic masked headline */}
        <h1
          data-testid="hero-headline"
          className="font-display text-[14vw] font-semibold uppercase leading-[0.88] tracking-tighter text-slate-50 sm:text-[11vw] lg:text-[8.2vw]"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.06em]">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, ease: EASE, delay: 0.45 + i * 0.13 }}
                className={`block ${i === 1 ? "text-uv" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Sub + CTA row */}
        <div className="mt-10 flex flex-col gap-8 border-t border-white/10 pt-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className="max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtext"
          >
            In-house &amp; mobile tubing, drill pipe, and tool inspection — blacklight, cleaning &amp; coating,
            water blasting, storage, and trucking. Based in Church Point, Louisiana.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.3 }}
            className="flex flex-col gap-4"
          >
            <span className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.3em] text-uv" data-testid="hero-247-badge">
              <LiveDot /> Crews on call — 24/7 / 365
            </span>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="group inline-flex items-center gap-4 bg-uv px-7 py-5 font-display text-2xl font-semibold tracking-tight text-machine transition-colors duration-300 hover:bg-slate-50 sm:text-3xl"
            >
              <Phone className="h-6 w-6 transition-transform duration-300 group-hover:rotate-12" />
              {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1 }}
          className="mt-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowDown className="h-3.5 w-3.5" />
          </motion.span>
          Scroll — the yard never sleeps
        </motion.div>
      </motion.div>
    </section>
  );
}
